import { definedMap, proveNever } from "@glideapps/ts-necessities";
import chroma from "chroma-js";
import classNames from "classnames";
import { isEmptyOrUndefinedish } from "common/Support";
import type { WireListCardCollection } from "fluent-components/fluent-components";
import { useWireAppTheme } from "hooks/use-wireapp-theme";
import type { GlideFC } from "lib/glide-fc";
import * as React from "react";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import tw from "twin.macro";
import { css } from "webapp/styled-components";
import { WireButton } from "webapp/ui/views/wire-button/wire-button";
import { useSectionStyle } from "webapp/ui/views/wire-container/wire-container";
import { WireListContainer } from "webapp/ui/views/wire-list-container/wire-list-container";
import { useResponsiveSizeClass } from "webapp/wire-renderers/responsive-provider";
import { WireSectionThemer } from "webapp/wire-renderers/use-wire-overlay";
import { extractActions } from "webapp/wire-renderers/wire-lib";
import type { WireRenderer } from "webapp/wire-renderers/wire-renderer";
import type { UIAlignment, UIImageStyle, UISizeSimple } from "wire/types";
import {
    CardStyle,
    UIAspect,
    UIBackgroundStyle,
    UIButtonAppearance,
    UIImageFill,
    UISize,
    UIStyleVariant,
} from "wire/types";
import { Article } from "./card-collection-article";
import { Card, CardContainer } from "./card-collection-card";
import { List } from "./card-collection-list";
import { Table } from "./card-collection-table";
import { TableHeader } from "./card-collection-table-header";
import type { ItemDescription } from "./card-collection-types";

function rangeForPage(page: number, numPages: number, size: number): (number | null)[] {
    let start = page - size;
    let end = page + size;

    const upper = size * 2;

    if (start < 0) {
        end += -start;
        start = 0;
    }
    if (end >= numPages) {
        const d = end - (numPages - 1);
        start = Math.max(0, start - d);
        end = numPages - 1;
    }

    const result: (number | null)[] = [];
    for (let x = start; x <= end; x++) {
        result.push(x);
    }

    if (result[0] !== 0) {
        result[0] = 0;
        result[1] = null;
    }

    if (numPages > upper && result[upper] !== numPages - 1) {
        result[upper] = numPages - 1;
        result[upper - 1] = null;
    }

    return result;
}

interface Props<T> {
    readonly cardStyle: CardStyle;
    readonly pageItems: readonly T[];
    readonly page: number;
    readonly size: UISizeSimple;
    readonly headerTitle?: string;
    readonly headerSubtitle?: string;
    readonly headerEmphasis?: string;
    readonly imageStyle: UIImageStyle;
    readonly imageFill?: UIImageFill;
    readonly imageSize: UISize;
    readonly alignment: UIAlignment;
    readonly aspect: UIAspect;
    readonly styleVariant: UIStyleVariant;
    readonly numPages: number;
    readonly describeItem: (item: T) => ItemDescription;
    readonly onPageChange?: (page: number) => void;
}

export function CardCollection<T>(p: Props<T>): React.ReactElement | null {
    const {
        describeItem,
        numPages,
        page,
        pageItems,
        onPageChange,
        cardStyle,
        imageFill = UIImageFill.Cover,
        size,
        aspect,
        imageStyle,
        imageSize,
        alignment,
        styleVariant,
        headerTitle,
        headerSubtitle,
        headerEmphasis,
    } = p;

    let content: React.ReactNode;
    if (cardStyle === CardStyle.Table) {
        content = (
            <div
                tw="overflow-x-auto -mx-5 self-stretch pt-1 pb-3
                    gp-sm:-mx-6
                    gp-md:-mx-7
                    gp-lg:-mx-1">
                <div
                    tw="inline-block min-w-full px-5
                        gp-sm:px-6
                        gp-md:px-7
                        gp-lg:px-1">
                    <div
                        tw="shadow-md rounded-lg overflow-hidden"
                        className={styleVariant}
                        css={css`
                            &.${UIStyleVariant.Minimal} {
                                ${tw`shadow-none rounded-none`}
                            }
                        `}>
                        <WireSectionThemer
                            backgroundStyle={
                                styleVariant === UIStyleVariant.Minimal
                                    ? UIBackgroundStyle.None
                                    : UIBackgroundStyle.Highlight
                            }
                            tw="min-w-full width[700px] flex flex-col">
                            {!isEmptyOrUndefinedish(headerTitle ?? headerSubtitle ?? headerEmphasis) && (
                                <TableHeader
                                    desc={{
                                        ...describeItem(pageItems[0]),
                                        title: headerTitle,
                                        subtitle: headerSubtitle,
                                        emphasis: headerEmphasis,
                                    }}
                                    imageStyle={imageStyle}
                                />
                            )}
                            {pageItems.map((item, index) => {
                                const desc = describeItem(item);
                                const key = `${index}-${page}`;
                                return (
                                    <Table key={key} desc={desc} imageStyle={imageStyle} styleVariant={styleVariant} />
                                );
                            })}
                        </WireSectionThemer>
                    </div>
                </div>
            </div>
        );
    } else if (cardStyle === CardStyle.Cover) {
        content = (
            <CardContainer size={size}>
                {pageItems.map((item, index) => {
                    const desc = describeItem(item);
                    const key = `${index}-${page}`;
                    return (
                        <Card
                            key={key}
                            desc={desc}
                            size={size}
                            imageFill={UIImageFill.Cover}
                            imageStyle={imageStyle}
                            imageSize={imageSize}
                            alignment={alignment}
                            aspect={aspect}
                            viewType="grid"
                        />
                    );
                })}
            </CardContainer>
        );
    } else if (cardStyle === CardStyle.Card) {
        content = (
            <CardContainer size={size}>
                {pageItems.map((item, index) => {
                    const desc = describeItem(item);
                    const key = `${index}-${page}`;
                    return (
                        <Card
                            key={key}
                            desc={desc}
                            size={size}
                            imageFill={imageFill}
                            imageStyle={imageStyle}
                            imageSize={imageSize}
                            alignment={alignment}
                            aspect={aspect}
                            viewType="card"
                        />
                    );
                })}
            </CardContainer>
        );
    } else {
        const styles =
            styleVariant === UIStyleVariant.Minimal
                ? css`
                      ${tw`grid w-full grid-cols-1`}
                  `
                : css`
                      ${tw`grid w-full grid-cols-1 gap-4`}
                  `;
        let Component = Article;
        if (cardStyle === CardStyle.Article) {
            Component = Article;
        } else if (cardStyle === CardStyle.List) {
            Component = List;
        } else {
            Component = proveNever(cardStyle, "Unknown card style", List);
        }
        content = (
            <WireSectionThemer
                backgroundStyle={
                    styleVariant === UIStyleVariant.Minimal ? UIBackgroundStyle.None : UIBackgroundStyle.Highlight
                }
                css={styles}
                className={classNames(size, cardStyle)}>
                {pageItems.map((item, index) => {
                    const desc = describeItem(item);
                    const key = `${index}-${page}`;
                    return <Component key={key} desc={desc} imageStyle={imageStyle} styleVariant={styleVariant} />;
                })}
            </WireSectionThemer>
        );
    }

    return (
        <div tw="flex flex-col items-center">
            {content}
            {numPages > 1 && <Pager numPages={numPages} page={page} onPageChange={onPageChange} />}
        </div>
    );
}

interface PagerProps {
    readonly page: number;
    readonly numPages: number;
    readonly onPageChange?: (page: number) => void;
    readonly className?: string;
}

export const Pager: GlideFC<PagerProps> = p => {
    const { numPages, page, onPageChange } = p;

    const theme = useWireAppTheme();
    const sectionStyle = useSectionStyle();
    const sizeClass = useResponsiveSizeClass();

    let numItems = 2;
    if (sizeClass === "md") {
        numItems = 3;
    } else if (sizeClass === "lg") {
        numItems = 4;
    } else if (sizeClass === "xl" || sizeClass === "2xl") {
        numItems = 5;
    }

    const selectedBackgroundColor =
        sectionStyle === UIBackgroundStyle.Accent
            ? "rgba(255, 255, 255, 0.8)"
            : chroma(theme.primaryAccentColor).alpha(0.1).css();

    return (
        <div className={p.className} tw="flex mt-4 self-stretch justify-center">
            <WireButton
                appearance={UIButtonAppearance.Bordered}
                disabled={page === 0}
                tw="max-width[320px] rounded-lg px-2! mr-2 h-9
                    gp-sm:px-3
                    gp-lg:(mr-6 flex-grow)"
                onClick={() => onPageChange?.(Math.max(0, page - 1))}>
                <div
                    tw="hidden
                        gp-lg:block">
                    Previous
                </div>
                <div tw="gp-lg:hidden">
                    <GlideIcon iconSize={16} icon="st-caret" rotateDeg={90} kind="stroke" />
                </div>
            </WireButton>
            <div tw="flex shadow-md rounded-lg overflow-hidden all-child:(px-1 py-2 bg-n0 h-9)">
                {rangeForPage(page, numPages, numItems).map((x, ind) => (
                    <button
                        key={ind}
                        onClick={() => x !== null && onPageChange?.(x)}
                        style={{
                            backgroundColor: x === page ? selectedBackgroundColor : undefined,
                        }}
                        disabled={x === null}
                        tw="text-text-base transition-colors last:(border-r-0) hover:(text-text-dark bg-n50)
                            focus-visible:underline rounded-none border-0 border-r border-solid border-border-base
                            disabled:(hover:bg-n0 text-text-base cursor-auto)"
                        className={x === page ? "selected-page-number" : undefined}
                        css={css`
                            &.selected-page-number {
                                ${tw`font-bold text-accent`}
                            }
                        `}>
                        <div
                            tw="w-5 text-center
                                gp-sm:w-7">
                            {x === null ? "…" : x + 1}
                        </div>
                    </button>
                ))}
            </div>
            <WireButton
                appearance={UIButtonAppearance.Bordered}
                disabled={page === numPages - 1}
                onClick={() => onPageChange?.(Math.min(numPages - 1, page + 1))}
                tw="max-width[320px] rounded-lg px-2! ml-2 h-9
                    gp-sm:px-3
                    gp-lg:(ml-6 flex-grow)">
                <div
                    tw="hidden
                        gp-lg:block">
                    Next
                </div>
                <div tw="gp-lg:hidden">
                    <GlideIcon iconSize={16} icon="st-caret" rotateDeg={-90} kind="stroke" />
                </div>
            </WireButton>
        </div>
    );
};

export const CardCollectionRenderer: WireRenderer<WireListCardCollection> = React.memo(p => {
    const { backend } = p;
    const pageIndexToken = p.paging?.pageIndex.onChangeToken;

    return (
        <WireListContainer
            title={p.componentTitle ?? ""}
            titleActions={extractActions(p.titleActions, backend)}
            filterValue={definedMap(
                p.dynamicFilterCurrentIndex,
                i => p.dynamicFilterNames?.[i.value]?.[0] ?? undefined
            )}
            filterItems={p.dynamicFilterNames}
            onFilterChange={definedMap(p.dynamicFilterCurrentIndex?.onChangeToken, t => (newIndex: number) => {
                backend.valueChanged(t, newIndex);
            })}
            searchValue={p.searchBar?.value}
            onSearchChange={definedMap(p.searchBar?.onChangeToken, x => (newVal: string) => {
                backend.valueChanged(x, newVal);
                // Changing the search makes paging jump to the first page.
                if (pageIndexToken !== undefined) {
                    backend.valueChanged(pageIndexToken, 0);
                }
            })}
            styleVariant={UIStyleVariant.Default}
            appKind={backend.appKind}>
            <CardCollection
                pageItems={p.items}
                size={p.size ?? UISize.Small}
                numPages={p.paging?.numPages ?? 1}
                page={p.paging?.pageIndex.value ?? 0}
                aspect={p.aspectRatio ?? UIAspect.SixteenByNine}
                imageStyle={p.imageStyle}
                imageSize={p.imageSize}
                alignment={p.alignment}
                imageFill={p.imageFill}
                styleVariant={p.style ?? UIStyleVariant.Default}
                headerTitle={p.headerTitle ?? ""}
                headerSubtitle={p.headerSubtitle ?? ""}
                headerEmphasis={p.headerEmphasis ?? ""}
                describeItem={x => {
                    const { action, itemActions, ...rest } = x;

                    return {
                        ...rest,
                        onCardClick: definedMap(action?.token, token => () => backend.runAction(token, false)),
                        menuItems: extractActions(itemActions, backend),
                    };
                }}
                cardStyle={p.cardStyle}
                onPageChange={definedMap(pageIndexToken, t => pageIndex => {
                    backend.valueChanged(t, pageIndex);
                })}
            />
        </WireListContainer>
    );
});
